import { useEffect, useState, useCallback } from "react";
import { X, User, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectSiteHeaderColor,
  selectSiteLoginColor,
  selectSiteSignUpColor,
  selectSiteLogo,
  selectDomainID, // Fixed naming convention
  selectSiteName,
  selectSiteFevicon,
} from "./../../../AsianExchangeSrc/utils/helper/commonSelectors";
import { loginSuccess } from "../../../AsianExchangeSrc/redux/slices/userSlice";
import { setAccountType } from "../../../AsianExchangeSrc/redux/slices/accountSlice";
import {
  login,
  getCurrentRecord,
  createLoginRecord,
} from "../../../AsianExchangeSrc/api";
import SiteLogo from "../../../CommonComponents/SiteLogo";
import Loader from "../../../AsianExchangeSrc/utils/Loader";
import { generatePresignedUrl } from "../../../AsianExchangeSrc/utils/helper/generatePresignedUrl";
import { usePrimaryColor } from "../../hooks/useThemeColors";


export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const domainId = useSelector(selectDomainID()); 
  const headerColor = useSelector(selectSiteHeaderColor());
  const loginBtnColor = useSelector(selectSiteLoginColor());
  const signUpBtnColor = useSelector(selectSiteSignUpColor());
  const siteName = useSelector(selectSiteName());
  const siteLogo = useSelector(selectSiteLogo());
  const siteFavIcon = useSelector(selectSiteFevicon());
  const [logoUrl, setLogoUrl] = useState(null);
  const [favIconUrl, setFavIconUrl] = useState(null);
  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    domainId: domainId || "", // Ensure domainId is not undefined
    x_panel_type: "user",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState({ demo: false, real: false });
  const previousRoute = useSelector((state) => state.route.previousRoute);
  const PrimaryColor = usePrimaryColor()

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.userName.trim()) newErrors.userName = "Username is required";
    if (!formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      setLoading((prev) => ({ ...prev, real: true }));
      try {
        const response = await login(formData);
        if (response.success) {
          const { token, userdata } = response.data;

          localStorage.setItem("userId", userdata?._id);
          dispatch(
            loginSuccess({ userData: userdata, loggedInType: "real", token })
          );
          dispatch(setAccountType({ userData: userdata, type: "real" }));


          // Run navigation and record creation concurrently
          await getCurrentRecord().then((res) => createLoginRecord(res.data));
          navigate(previousRoute || "/", { replace: true }); // Navigate after record creation
           toast.success(response.data.message || "Login successful");

        } else {
          toast.error(response.data.message || "Invalid credentials");
        }
      } catch (error) {
        console.error("Login API error:", error);
        toast.error("Failed to login. Please try again.");
      } finally {
        setLoading((prev) => ({ ...prev, real: false }));
      }
    },
    [dispatch, formData, navigate, validateForm, previousRoute]
  );

  const handleDemoAccount = useCallback(async () => {
    setLoading((prev) => ({ ...prev, demo: true }));
    try {
      // TODO: Replace with actual API call for demo account creation
      const res = {
        data: {
          token: null,
          userdata: {
            _id: "Demo",
            name: "Demo",
            userName: "Democlient",
            commission: 0,
            openingBalance: 0,
            creditReference: 0,
            partnership: 0,
            currency: "INR",
            mobile: "NA",
            exposureLimit: 0,
            exposure: "00",
            availableBalance: "000",
            refProfitLoss: 0,
            domain: "NA",
            status: "active",
            deleted: false,
            deviceIp: "NA",
            isOnline: false,
            createdBy: "NA",
            ownerDomainId: "NA",
            isVerified: true,
            createdAt: new Date().toISOString(),
            isReadOnly: false,
          },
        },
      };

      const { token, userdata } = res.data;

      dispatch(setAccountType({ type: "demo" }));
      dispatch(
        loginSuccess({
          userData: userdata,
          loggedInType: "demo",
          token,
        })
      );

      toast.success("Demo account created successfully");
      navigate(previousRoute || "/", { replace: true }); // Consistent navigation
    } catch (error) {
      console.error("Error creating demo account:", error);
      toast.error("Failed to create demo account. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, demo: false }));
    }
  }, [dispatch, navigate, previousRoute]);

  useEffect(() => {
    if (!siteLogo && !siteFavIcon && !siteName) return;

    const fetchAssets = async () => {
      try {
        const [logoPresigned, favIconPresigned] = await Promise.all([
          siteLogo ? generatePresignedUrl(siteLogo) : Promise.resolve(null),
          siteFavIcon
            ? generatePresignedUrl(siteFavIcon)
            : Promise.resolve(null),
        ]);

        if (logoPresigned) setLogoUrl(logoPresigned);
        if (favIconPresigned) setFavIconUrl(favIconPresigned);
        if (siteName) document.title = siteName;
      } catch (error) {
        console.error("Error fetching site assets:", error);
        toast.error("Failed to load site assets. Please try again.");
      }
    };

    fetchAssets();
  }, [siteLogo, siteFavIcon, siteName]);

  return (
    <div
      className="h-screen overflow-hidden bg-gradient-to-b flex flex-col items-center justify-center p-4 px-10 sm:px-0 relative"
      style={{
        backgroundImage: `linear-gradient(180deg, ${
          headerColor?.start || "#000"
        }, ${headerColor?.end || "#000"})`,
      }}
    >
      <button
        className="absolute top-6 right-6 text-black p-1 text-bold rounded-md hover:text-gray-700 transition-colors"
        onClick={() => navigate("/")}
        style={{
          backgroundImage: `linear-gradient(180deg, ${
            headerColor?.start || "#000"
          }, ${headerColor?.end || "#000"})`,
        }}
      >
        <X size={24} />
      </button>

      <div className="w-full max-w-sm space-y-8">
        <div className="flex justify-center">
          {logoUrl && <img src={logoUrl} alt={siteName} className="h-14" />}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              name="userName"
              placeholder="Username"
              value={formData.userName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.userName ? "border-red-500" : ""
              }`}
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <User size={20} className="text-gray-600" fill="black" />
            </div>
            {errors.userName && (
              <p className="text-red-500 text-xs mt-1">{errors.userName}</p>
            )}
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.password ? "border-red-500" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {showPassword ? (
                <EyeOff size={20} fill="black" />
              ) : (
                <Eye size={20} />
              )}
            </button>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          <div className="space-y-1.5 pt-4">
            <button
              type="submit"
              className="w-full py-3 text-white font-semibold rounded-lg transition-colors duration-200 flex justify-center items-center space-x-3"
              disabled={loading.real}
              style={{
                background: `linear-gradient(180deg, ${
                  loginBtnColor?.start || "#045662"
                }, ${loginBtnColor?.end || "#045662"})`,
              }}
            >
              {loading.real ? (
                <>
                  <Loader />
                  <span>Logging in...</span>
                </>
              ) : (
                <span>Login</span>
              )}
            </button>

            <button
              type="button"
              onClick={handleDemoAccount}
              className="w-full py-3 text-white font-semibold rounded-lg transition-colors duration-200 flex justify-center items-center space-x-3"
              disabled={loading.demo}
              style={{
                background: `linear-gradient(180deg, ${
                  signUpBtnColor?.start || "#045662"
                }, ${signUpBtnColor?.end || "#045662"})`,
              }}
            >
              {loading.demo ? (
                <>
                  <Loader />
                  <span>Creating Demo...</span>
                </>
              ) : (
                <span>Login with Demo ID</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
